import { useMemo, useState, useDeferredValue } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronsUp,
  ChevronsDown,
  Mail,
  Search,
  UserPlus,
  XCircle,
  Undo2,
} from "lucide-react";

import { Card } from "../../components/Card/Card";
import { Button } from "../../components/Button/Button";
import { Input } from "../../components/Input/Input";
import { Avatar } from "../../components/Avatar/Avatar";
import { Badge } from "../../components/Badge/Badge";
import { EmptyState } from "../../components/EmptyState/EmptyState";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { Menu, MenuItem, MenuSeparator } from "../../components/Menu/Menu";
import { useToast } from "../../components/Toast/Toast";

import { useAuth } from "../auth/useAuth";
import { ApiError, describeError } from "../../lib/errors";
import { AdminUser, listUsers, sendPasswordReset } from "./api";
import { humanizeRelativeDate } from "./relativeTime";
import { InviteAdminModal } from "./InviteAdminModal";
import { PromoteModal } from "./PromoteModal";
import { DemoteModal } from "./DemoteModal";
import { DisableModal } from "./DisableModal";
import { ReEnableModal } from "./ReEnableModal";
import styles from "./UsersPage.module.css";

type ActionKind = "promote" | "demote" | "disable" | "enable";
type RoleFilter = "all" | "super_admin" | "project_user";
type StatusFilter = "all" | "active" | "disabled";

interface ActionState {
  kind: ActionKind;
  target: AdminUser;
}

interface FilterChipProps {
  label: string;
  value: string;
  items: MenuItem[];
}

function FilterChip({ label, value, items }: FilterChipProps) {
  return (
    <Menu
      items={items}
      placement="bottom-start"
      aria-label={`${label} filter`}
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`${label} filter — ${value}`}
          className={styles.filterChip}
          onClick={toggle}
        >
          <span className={styles.filterChipLabel}>{label}:</span>
          <span className={styles.filterChipValue}>{value}</span>
          <ChevronDown
            width={11}
            height={11}
            aria-hidden="true"
            className={styles.filterChipIcon}
          />
        </button>
      )}
    />
  );
}

function roleBadge(u: AdminUser) {
  return u.role === "super_admin" ? (
    <Badge variant="admin">Super admin</Badge>
  ) : (
    <Badge variant="user">Member</Badge>
  );
}

interface ProjectsCellProps {
  user: AdminUser;
  totalProjectsCount: number;
}

function ProjectsCell({ user, totalProjectsCount }: ProjectsCellProps) {
  if (user.role === "super_admin") {
    return (
      <span className={styles.muted}>All ({totalProjectsCount})</span>
    );
  }
  if (
    user.projects_count === 0 &&
    user.status === "active" &&
    !user.last_login_at
  ) {
    return (
      <span className={styles.muted}>
        0 <span className={styles.warn}>(pending invite)</span>
      </span>
    );
  }
  const label =
    user.projects_count === 1
      ? "1 project"
      : `${user.projects_count} projects`;
  return <span className={styles.muted}>{label}</span>;
}

function statusBadge(u: AdminUser) {
  if (u.status === "disabled") {
    return (
      <Badge variant="neutral" dot>
        Disabled
      </Badge>
    );
  }
  // Active row with no last login + no projects → still in setup.
  if (u.projects_count === 0 && !u.last_login_at) {
    return <Badge variant="warning">Setup</Badge>;
  }
  return <Badge variant="success">Active</Badge>;
}

interface UserCellProps {
  user: AdminUser;
  isSelf: boolean;
}

function UserCell({ user, isSelf }: UserCellProps) {
  const disabled = user.status === "disabled";
  return (
    <div className={styles.userCell}>
      <Avatar name={user.name} size={32} />
      <div className={styles.userCellInfo}>
        <div
          className={`${styles.userCellName} ${
            disabled ? styles.strike : ""
          }`}
        >
          <span>{user.name}</span>
          {isSelf && <span className={styles.youMark}>you</span>}
        </div>
        <div className={styles.userCellEmail}>{user.email}</div>
      </div>
    </div>
  );
}

/**
 * Sort comparator used by the table:
 * - active rows first, disabled rows last;
 * - within each bucket, most recent last_login first;
 * - rows without last_login go to the bottom of their bucket.
 */
function compareUsers(a: AdminUser, b: AdminUser): number {
  const aDis = a.status === "disabled" ? 1 : 0;
  const bDis = b.status === "disabled" ? 1 : 0;
  if (aDis !== bDis) return aDis - bDis;
  const aT = a.last_login_at ? new Date(a.last_login_at).getTime() : -Infinity;
  const bT = b.last_login_at ? new Date(b.last_login_at).getTime() : -Infinity;
  if (aT !== bT) return bT - aT;
  return a.name.localeCompare(b.name);
}

export function UsersPage() {
  const { push } = useToast();
  const { user: me, isLastSuperAdmin, projects } = useAuth();

  const [inviteOpen, setInviteOpen] = useState(false);
  const [action, setAction] = useState<ActionState | null>(null);

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: listUsers,
  });

  const resetMut = useMutation({
    mutationFn: (u: AdminUser) => sendPasswordReset(u.id),
    onSuccess: (_data, variables) => {
      push({
        variant: "success",
        message: `Password reset sent to ${variables.email}.`,
      });
    },
    onError: (err: unknown) => {
      push({
        variant: "danger",
        message:
          err instanceof ApiError
            ? describeError(err.code)
            : describeError("internal"),
      });
    },
  });

  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    let rows = users.slice();
    if (q) {
      rows = rows.filter(
        (u) =>
          u.name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q),
      );
    }
    if (roleFilter !== "all") {
      rows = rows.filter((u) => u.role === roleFilter);
    }
    if (statusFilter !== "all") {
      rows = rows.filter((u) => u.status === statusFilter);
    }
    rows.sort(compareUsers);
    return rows;
  }, [users, deferredQuery, roleFilter, statusFilter]);

  const header = (
    <PageHeader
      breadcrumb={[{ label: "Platform" }, { label: "Users" }]}
      title="Platform users"
      actions={
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus size={14} aria-hidden="true" />
          <span>Invite admin</span>
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <>
        {header}
        <div className={styles.page}>
          <div className={styles.loading}>Loading…</div>
        </div>
      </>
    );
  }

  const roleFilterLabel: Record<RoleFilter, string> = {
    all: "All",
    super_admin: "Super admin",
    project_user: "Member",
  };
  const statusFilterLabel: Record<StatusFilter, string> = {
    all: "All",
    active: "Active",
    disabled: "Disabled",
  };

  const roleFilterItems: MenuItem[] = (
    ["all", "super_admin", "project_user"] as RoleFilter[]
  ).map((v) => ({
    label: roleFilterLabel[v],
    onSelect: () => setRoleFilter(v),
  }));
  const statusFilterItems: MenuItem[] = (
    ["all", "active", "disabled"] as StatusFilter[]
  ).map((v) => ({
    label: statusFilterLabel[v],
    onSelect: () => setStatusFilter(v),
  }));

  const totalProjects = projects.length;

  function rowMenu(u: AdminUser): (MenuItem | MenuSeparator)[] {
    const isSelf = u.id === me?.id;
    // Disabled row → only re-enable.
    if (u.status === "disabled") {
      return [
        {
          label: "Re-enable account",
          icon: <Undo2 size={13} aria-hidden="true" />,
          onSelect: () => setAction({ kind: "enable", target: u }),
        },
      ];
    }

    // Self-row — render a minimal menu so the kebab isn't empty but skip
    // destructive actions (mockup L750-754; v1 documents the rest as deferred).
    if (isSelf) {
      const items: (MenuItem | MenuSeparator)[] = [];
      if (u.role === "project_user") {
        items.push({
          label: "Promote to Super admin",
          icon: <ChevronsUp size={13} aria-hidden="true" />,
          onSelect: () => setAction({ kind: "promote", target: u }),
        });
      }
      items.push({
        label: "Send password reset",
        icon: <Mail size={13} aria-hidden="true" />,
        onSelect: () => resetMut.mutate(u),
      });
      // Last-super-admin guard — show demote as disabled with tooltip-like
      // label hint when applicable.
      if (u.role === "super_admin") {
        items.push({ separator: true });
        items.push({
          label: isLastSuperAdmin
            ? "Demote to Member — last super admin"
            : "Demote to Member",
          icon: <ChevronsDown size={13} aria-hidden="true" />,
          variant: "danger",
          disabled: isLastSuperAdmin,
          onSelect: () => setAction({ kind: "demote", target: u }),
        });
      }
      // Disable on self is always disabled (mockup notes).
      items.push({
        label: "Disable account — you can't disable yourself",
        icon: <XCircle size={13} aria-hidden="true" />,
        variant: "danger",
        disabled: true,
        onSelect: () => undefined,
      });
      return items;
    }

    // Other-user rows.
    if (u.role === "project_user") {
      return [
        {
          label: "Promote to Super admin",
          icon: <ChevronsUp size={13} aria-hidden="true" />,
          onSelect: () => setAction({ kind: "promote", target: u }),
        },
        {
          label: "Send password reset",
          icon: <Mail size={13} aria-hidden="true" />,
          onSelect: () => resetMut.mutate(u),
        },
        { separator: true },
        {
          label: "Disable account",
          icon: <XCircle size={13} aria-hidden="true" />,
          variant: "danger",
          onSelect: () => setAction({ kind: "disable", target: u }),
        },
      ];
    }
    // Other super_admin rows.
    return [
      {
        label: "Send password reset",
        icon: <Mail size={13} aria-hidden="true" />,
        onSelect: () => resetMut.mutate(u),
      },
      { separator: true },
      {
        label: "Demote to Member",
        icon: <ChevronsDown size={13} aria-hidden="true" />,
        variant: "danger",
        onSelect: () => setAction({ kind: "demote", target: u }),
      },
      {
        label: "Disable account",
        icon: <XCircle size={13} aria-hidden="true" />,
        variant: "danger",
        onSelect: () => setAction({ kind: "disable", target: u }),
      },
    ];
  }

  const hasRows = filtered.length > 0;

  return (
    <>
      {header}
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <Input
              type="search"
              placeholder="Search by email or name…"
              aria-label="Search users"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              leadingIcon={<Search size={14} aria-hidden="true" />}
            />
          </div>
          <FilterChip
            label="Role"
            value={roleFilterLabel[roleFilter]}
            items={roleFilterItems}
          />
          <FilterChip
            label="Status"
            value={statusFilterLabel[statusFilter]}
            items={statusFilterItems}
          />
        </div>

        <Card className={styles.tableCard}>
          {users.length === 0 ? (
            <EmptyState
              title="No platform admins yet"
              description="Invite the first admin to get started."
              action={
                <Button onClick={() => setInviteOpen(true)}>
                  <UserPlus size={14} aria-hidden="true" />
                  <span>Invite admin</span>
                </Button>
              }
            />
          ) : !hasRows ? (
            <EmptyState
              title="No users match your filters"
              description="Try a different search term or relax the role / status filter."
            />
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ width: "36%" }}>User</th>
                    <th>Role</th>
                    <th>Projects</th>
                    <th>Last login</th>
                    <th>Status</th>
                    <th
                      style={{ width: 60 }}
                      aria-label="Row actions"
                    />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => {
                    const isSelf = u.id === me?.id;
                    const isDisabled = u.status === "disabled";
                    const last = humanizeRelativeDate(u.last_login_at);
                    const lastLabel = u.last_login_at ? last : "never";
                    return (
                      <tr
                        key={u.id}
                        className={`${styles.row} ${
                          isDisabled ? styles.rowDisabled : ""
                        }`}
                      >
                        <td>
                          <UserCell user={u} isSelf={isSelf} />
                        </td>
                        <td>{roleBadge(u)}</td>
                        <td>
                          <ProjectsCell
                            user={u}
                            totalProjectsCount={totalProjects}
                          />
                        </td>
                        <td
                          className={
                            u.last_login_at ? styles.muted : styles.faint
                          }
                        >
                          {lastLabel}
                        </td>
                        <td>{statusBadge(u)}</td>
                        <td className={styles.menuCell}>
                          <Menu
                            items={rowMenu(u)}
                            aria-label={`Actions for ${u.name}`}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <InviteAdminModal
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
        />

        <PromoteModal
          open={action?.kind === "promote"}
          user={action?.kind === "promote" ? action.target : null}
          onClose={() => setAction(null)}
        />
        <DemoteModal
          open={action?.kind === "demote"}
          user={action?.kind === "demote" ? action.target : null}
          isLastSuperAdmin={isLastSuperAdmin}
          isSelf={action?.kind === "demote" && action.target.id === me?.id}
          onClose={() => setAction(null)}
        />
        <DisableModal
          open={action?.kind === "disable"}
          user={action?.kind === "disable" ? action.target : null}
          onClose={() => setAction(null)}
        />
        <ReEnableModal
          open={action?.kind === "enable"}
          user={action?.kind === "enable" ? action.target : null}
          onClose={() => setAction(null)}
        />
      </div>
    </>
  );
}
